/**
 * When to ask a guest to connect a social account.
 *
 * A guest account is real and permanent — but only on **this browser, on this
 * device**. It is keyed by a `device_uid` in `localStorage`. Clear site data,
 * reinstall, switch phones, or open the game in a different browser and the
 * progress is gone with no recovery path, because there is nothing to prove the
 * account was ever yours. Linking Google, Apple or Telegram is the only way to
 * make it portable — and it is also a hard precondition for withdrawing.
 *
 * So the prompt is not marketing, it is a data-loss warning. That framing
 * decides everything below:
 *
 * **It escalates with what is actually at risk.** A guest three minutes in has
 * 250 coins and nothing to lose; nagging them is noise. A guest with real Kyat
 * and a deep run has something they would genuinely grieve. The threshold falls
 * as the stake rises.
 *
 * **It always stays dismissible.** A modal a player cannot escape is how a game
 * gets uninstalled. The only place we refuse to take "no" for an answer is the
 * withdrawal flow, where linking is a server-side requirement rather than
 * advice.
 */

export interface NagInput {
  isGuest: boolean;
  /** Launches counted on this device. */
  sessions: number;
  coins: number;
  /** Kyat as the server sends it — a decimal string. */
  kyat: string;
  bestDepth: number;
  /** `Date.now()` of the last dismissal, or 0. */
  dismissedAt: number;
  now: number;
}

export type NagLevel =
  /** Say nothing. */
  | 'none'
  /** A quiet inline card on the menu. */
  | 'gentle'
  /** A card that names what would be lost. */
  | 'urgent';

/** Cool-off after a dismissal, per level. Urgent returns sooner — more at stake. */
const COOL_OFF_MS: Record<Exclude<NagLevel, 'none'>, number> = {
  gentle: 3 * 24 * 60 * 60 * 1000,
  urgent: 24 * 60 * 60 * 1000,
};

/** Kyat is withdrawable, so any at all makes the account worth protecting. */
export const URGENT_KYAT = 1;
export const URGENT_COINS = 5_000;
export const URGENT_DEPTH = 200;

/** Below this there is genuinely nothing to lose, so we stay quiet. */
export const FIRST_ASK_SESSION = 3;

export function parseKyat(kyat: string): number {
  const n = Number.parseFloat(kyat);
  return Number.isFinite(n) ? n : 0;
}

/**
 * How much this account is worth losing, 0–1. Drives the wording, so the
 * message can name the biggest thing at stake rather than listing everything.
 */
export function stake(input: NagInput): number {
  const k = Math.min(1, parseKyat(input.kyat) / (URGENT_KYAT * 10));
  const c = Math.min(1, input.coins / (URGENT_COINS * 4));
  const d = Math.min(1, input.bestDepth / (URGENT_DEPTH * 5));
  return Math.max(k, c, d);
}

export function nagLevel(input: NagInput): NagLevel {
  /* Linked accounts are already safe. */
  if (!input.isGuest) return 'none';

  const hasRealStake =
    parseKyat(input.kyat) >= URGENT_KYAT
    || input.coins >= URGENT_COINS
    || input.bestDepth >= URGENT_DEPTH;

  /* Brand-new players are left alone unless they have somehow already earned
     something worth keeping. */
  if (input.sessions < FIRST_ASK_SESSION && !hasRealStake) return 'none';

  const level: Exclude<NagLevel, 'none'> = hasRealStake ? 'urgent' : 'gentle';

  if (input.dismissedAt > 0 && input.now - input.dismissedAt < COOL_OFF_MS[level]) {
    return 'none';
  }
  return level;
}

/**
 * The single most valuable thing this player would lose.
 *
 * Naming one concrete thing they earned beats a generic "your progress" — it is
 * the difference between a warning and a banner people stop seeing.
 */
export function atRisk(input: NagInput): string {
  const kyat = parseKyat(input.kyat);
  if (kyat >= URGENT_KYAT) {
    return `${input.kyat} Shwe Kyat`;
  }
  if (input.bestDepth >= URGENT_DEPTH) {
    return `${input.bestDepth}m of depth`;
  }
  if (input.coins >= URGENT_COINS) {
    return `${input.coins.toLocaleString()} coins`;
  }
  return 'your progress';
}

export function nagCopy(level: NagLevel, input: NagInput): {
  title: string; body: string; action: string;
} | null {
  if (level === 'none') return null;

  if (level === 'urgent') {
    return {
      title: 'Protect your account',
      body: `You are playing as a guest, so ${atRisk(input)} lives only on this `
        + 'device. Connect an account to keep it — and to withdraw later.',
      action: 'Connect',
    };
  }
  return {
    title: 'Playing as a guest',
    body: 'Connect Google, Apple or Telegram so your progress follows you to a '
      + 'new phone. Nothing resets — you keep everything.',
    action: 'Connect',
  };
}

/* ── session counting ─────────────────────────────────────────────────────── */

const SESSIONS_KEY = 'shwetap.sessions';
const NAG_KEY = 'shwetap.link.dismissed';

export function bumpSessions(): number {
  try {
    const n = Number(localStorage.getItem(SESSIONS_KEY) ?? '0') + 1;
    localStorage.setItem(SESSIONS_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}

export function readSessions(): number {
  try { return Number(localStorage.getItem(SESSIONS_KEY) ?? '0'); } catch { return 0; }
}

export function readDismissed(): number {
  try { return Number(localStorage.getItem(NAG_KEY) ?? '0'); } catch { return 0; }
}

export function markNagDismissed(now: number = Date.now()): void {
  try { localStorage.setItem(NAG_KEY, String(now)); } catch { /* private mode */ }
}

/** After a successful link there is nothing left to warn about. */
export function clearNag(): void {
  try { localStorage.removeItem(NAG_KEY); } catch { /* private mode */ }
}
